import { api, descargarArchivo } from "@/lib/api"

/**
 * Descargar en Excel o CSV lo que se está mirando.
 *
 *   GET /api/export/catalogo          qué se puede bajar y con qué parámetros
 *   GET /api/export/{recurso}         el archivo
 *
 * UN SOLO ENDPOINT para los diez recursos. Lo que cambia por recurso son los
 * parámetros, y el catálogo dice cuáles necesita cada uno: así una pantalla
 * nueva no tiene que saber nada de las demás, y añadir un recurso en el backend
 * no obliga a tocar la SPA.
 */

export type Seccion = "contabilidad" | "finanzas" | "declaraciones"

export interface RecursoExportable {
  id: string
  seccion: Seccion
  nombre: string
  descripcion: string
  /** Nombres exactos de los parámetros obligatorios. */
  requiere: string[]
  capacidad: string
}

export interface CatalogoExport {
  recursos: RecursoExportable[]
  /** Sin `openpyxl` en el servidor esto viene como `["csv"]`. */
  formatos: Array<"xlsx" | "csv">
  nota: string
  xlsx_disponible: boolean
  nota_xlsx?: string
}

export function fetchCatalogoExport(): Promise<CatalogoExport> {
  return api.get<CatalogoExport>("/export/catalogo")
}

/**
 * Los filtros que puede llevar una descarga. Todos opcionales aquí; el backend
 * rechaza con 422 y NOMBRA los que falten para ese recurso.
 */
export interface FiltrosExport {
  entity_id?: string | null
  libro?: string
  desde?: string
  hasta?: string
  anio?: number
  pais?: string
  origen?: string
  period_id?: string
  filing_code?: string
}

/**
 * Descarga el archivo.
 *
 * El nombre lo pone el backend en `Content-Disposition` —lleva el rango dentro,
 * para que tres descargas del mismo recurso no acaben como «diario.xlsx»,
 * «diario (1).xlsx» y «diario (2).xlsx»—. El que se pasa aquí es sólo el
 * respaldo por si la cabecera no llega.
 */
export function descargar(
  recursoId: string,
  formato: "xlsx" | "csv",
  filtros: FiltrosExport = {},
  /**
   * El tenant del dato, cuando no es el activo. Solo lo usa la consola del
   * contador, que es cross-tenant: ver `descargarArchivo` en `lib/api.ts`.
   */
  tenantId?: string,
): Promise<void> {
  const query = new URLSearchParams({ formato })
  for (const [clave, valor] of Object.entries(filtros)) {
    if (valor === undefined || valor === null || valor === "") continue
    query.set(clave, String(valor))
  }
  return descargarArchivo(
    `/export/${recursoId}?${query.toString()}`,
    `${recursoId}.${formato}`,
    tenantId,
  )
}
